import { Grid } from '@mui/material'

import { useFetchFirmDashboardBillingQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { formatCurrency } from '../../../utils'
import DashboardBarChart from './DashboardBarChart'
import SectionTitle from './SectionTitle'
import StatCard from './StatCard'
import { formatCount, formatRangeLabel, toAmountBars, toCountBars, withRange } from './constants'

interface BillingTabProps {
  startDate: string
  endDate: string
}

const BILLING_TYPE_TOOLTIP =
  "By the matter's current billing type. Contingency includes Contingency Hybrid; Other covers Pro Bono, Consult and matters with no billing type."

const BillingTab = ({ startDate, endDate }: BillingTabProps) => {
  const { data, loading } = useFetchFirmDashboardBillingQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: { input: { startDate, endDate } }
  })

  const billing = data?.fetchFirmDashboardBilling?.response?.status === 200 ? data.fetchFirmDashboardBilling : null
  const rangeLabel = formatRangeLabel(startDate, endDate)

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SectionTitle
          title="Bill Counts"
          subtitle={`Bills issued ${rangeLabel}; pre-bills count only towards Bills Generated.`}
        />
      </Grid>

      <Grid item xs={12}>
        <StatCard
          title="Bills Generated"
          subtitle={withRange(
            'Excludes voided and forwarded bills. Pre-bills are bills still in draft or awaiting approval.',
            rangeLabel
          )}
          loading={loading}
          items={[
            { label: 'Total', value: formatCount(billing?.totalBillsCount ?? 0) },
            { label: 'Final Bills', value: formatCount(billing?.finalBillsCount ?? 0) },
            { label: 'Pre-bills', value: formatCount(billing?.preBillsCount ?? 0) }
          ]}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DashboardBarChart
          title="Bills by Billing Type"
          subtitle={withRange('Final bills; excludes voided and forwarded.', rangeLabel)}
          tooltip={BILLING_TYPE_TOOLTIP}
          data={toCountBars(billing?.billsByBillingType)}
          loading={loading}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <DashboardBarChart
          title="Bills by Status"
          subtitle={withRange('Final bills; excludes voided and forwarded.', rangeLabel)}
          tooltip="Paid includes Pre-paid; Unpaid is billed or sent to the client with nothing paid yet."
          data={toCountBars(billing?.billsByStatus)}
          loading={loading}
        />
      </Grid>
      <Grid item xs={12}>
        <DashboardBarChart
          title="Bills by Type of Law"
          subtitle={withRange('Final bills; excludes voided and forwarded.', rangeLabel)}
          tooltip="By the Type of Law of the bill's matter (from its case type). (No Law Type) covers matters without one."
          data={toCountBars(billing?.billsByLawType)}
          orientation="horizontal"
          categoryWidth={150}
          loading={loading}
        />
      </Grid>

      <Grid item xs={12}>
        <SectionTitle title="Bill Amounts" subtitle={`Bill principal (interest not included), ${rangeLabel}.`} />
      </Grid>

      <Grid item xs={12}>
        <StatCard
          title="Total Billed"
          subtitle={withRange('Final bills; excludes voided.', rangeLabel)}
          loading={loading}
          items={[{ label: 'Total', value: formatCurrency(billing?.totalBillsAmount ?? 0) }]}
        />
      </Grid>
      <Grid item xs={12}>
        <DashboardBarChart
          title="Bill Amount by Billing Type"
          subtitle={withRange('Final bills; excludes voided.', rangeLabel)}
          tooltip={BILLING_TYPE_TOOLTIP}
          data={toAmountBars(billing?.amountByBillingType)}
          valueType="currency"
          loading={loading}
        />
      </Grid>
      <Grid item xs={12}>
        <DashboardBarChart
          title="Billing Amount by Category"
          subtitle={withRange('Final bills; line amounts before bill discounts, plus interest.', rangeLabel)}
          tooltip="Monthly Flat Fee: flat fee instalments. Legal Fees: every other legal fee (initial and special flat fee payments, appearance fees…). Investigator: investigator time. Others: non-client or unlinked lines. Does not add up to Total Billed — lines are before bill discounts, and interest is extra."
          data={toAmountBars(billing?.amountByCategory)}
          valueType="currency"
          loading={loading}
        />
      </Grid>
    </Grid>
  )
}

export default BillingTab
